import * as React from 'react';
import { Predicate, pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';

import { useIORef } from './hooks';
import { safeFocus, safeBlur } from './Env';

export const GLOBAL_INPUT_CLASS = 'input';

export interface Model<T> {
    empty: T,
    putOut: (x: string, prev: T | null) => T,
    putIn: (x: T) => string,
}



const isEmptyT = <T extends any>(model: Model<T>) => 
    (value: T) => value === model.empty;


const clearT = <T extends any>(model: Model<T>) => 
    (): [T, string] => [model.empty, model.putIn(model.empty)];


const inputT = <T extends any>(model: Model<T>) => 
    (str: string, prev: T): [T, string] => 
        pipe(
            str,
            (x) => {
                const out = model.putOut(x, prev);
                const display = model.putIn(out);
                return [out, display];
            },
        );


export type Controller<T> = {
    value: T,
    displayValue: string,
    isEmpty: () => boolean,
    clear: () => void,
    put: (str: string) => void,
    input: (str: string) => void,
    output: (str: string) => void,
    inoutput: (str: string) => void,
}

export const useControl = function<T>(Input: Model<T>, init: T): Controller<T> {
    const { read, write } = useIORef(init);
    const [value, setValue] = React.useState(Input.putOut(String(init), null));
    const [displayValue, setDisplayValue] = React.useState(Input.putIn(init));


    React.useEffect(() => {
        setValue(prev => Input.putOut(String(init), prev));
        write(init)();
        setDisplayValue(Input.putIn(init));
    }, [init]);

    const { isEmpty, clear, input } = React.useMemo(() => ({
        isEmpty: isEmptyT(Input),
        clear: clearT(Input),
        input: inputT(Input),
    }), [Input]);

    return {
        value,
        displayValue,
        isEmpty: () => isEmpty(read()),
        clear: () => {
            const [value, displayValue] = clear();

            setValue(value);
            write(value)();
            setDisplayValue(displayValue);
        },
        put: (str) => {
            setDisplayValue(str);
        },
        input: (str: string) => {
            const [, display] = input(str, value);

            setDisplayValue(display);
        },
        output: (str: string) => {
            const [out] = input(str, value);
            
            write(out)();
            setValue(out);
        },
        inoutput: (str: string) => {
            const [out, display] = input(str, value);

            setValue(out);
            write(out)();
            setDisplayValue(display);
        },
    }
}


type Valid = 1;
type Error = -1;
type Indetermination = 0;

export type ErrorStatus = Error | Indetermination | Valid;
type ErrTuple = [Valid, null] | [Error, string];

export type ValidRules<T> = [Predicate<T>, string][];

const validatorLoop = <T extends any>(rules: ValidRules<T>) => 
    (x: T) => {
        return pipe(
            rules.find(([pred]) => !pred(x)),
            O.fromNullable,
            O.fold(
                (): ErrTuple => [1, null],
                ([, errMessage]): ErrTuple => [-1, errMessage],
            ),
        )
    }

const validateT = <T extends any>(model: Model<T>) => 
    (rules: ValidRules<T>) =>
        (str: string, prev: T): ErrTuple => 
            pipe(
                str,
                s => model.putOut(s, prev),
                validatorLoop(rules),
            );

const isValidT = <T extends any>(_: Model<T>) => 
    (status: ErrorStatus): status is Valid => status === 1;

const isIndeterminationT = <T extends any>(_: Model<T>) => 
    (status: ErrorStatus): status is Indetermination => status === 0;

const resetValidT = <T extends any>(_: Model<T>) => 
    (): Indetermination => 0;


interface IValidator {
    errMessage: string | null,
    validate: (str: string) => void,
    isValid: () => boolean,
    isIndetermination: () => boolean,
    resetValid: () => void,
}

export const withValidator = <T extends any>(controller: (Input: Model<T>, init: T) => Controller<T>, validationRules: ValidRules<T>) => {
    return (Input: Model<T>, init: T): Controller<T> & IValidator => {
    
        const [validStatus, setValidStatus] = React.useState<ErrorStatus>(0);
        const [errMessage, setErrMessage] = React.useState<string | null>(null);

        const { isValid, resetValid, validate, isIndetermination } = React.useMemo(() => ({
            isValid: isValidT(Input),
            isIndetermination: isIndeterminationT(Input),
            resetValid: resetValidT(Input),
            validate: validateT(Input)(validationRules),
        }), [validationRules]);

        const control = controller(Input, init);

        return {
            ...control,
            errMessage,
            validate: (str: string) => {
                const [status, errMessage] = validate(str, control.value);

                setValidStatus(status);
                setErrMessage(errMessage);
            },
            isValid: () => isValid(validStatus),
            isIndetermination: () => isIndetermination(validStatus),
            resetValid: () => {
                const s = resetValid();
    
                setValidStatus(s);
            },
        }
    }
}


export interface DirtyController {
    focus: () => void,
    blur: () => void,
}

export const useDirtyControl = (ref: React.RefObject<HTMLInputElement>) => {
    return {
        focus: () => {
            window.requestAnimationFrame(safeFocus(ref));
        },
        blur: () => {
            window.requestAnimationFrame(safeBlur(ref));
        },
    }
}
